import type { DevicesFilters, ScheduledReportsFilters } from "@/_store/filters.module.ts";
import cloneDeep from "lodash/cloneDeep";
import {
  defaultScheduledReportFilters,
  ScheduledType,
  type ScheduledExportItem,
  type ScheduledReportItem,
} from "@/_store/scheduled-reports/scheduled-reports.module.ts";
import { ActivityLogsType } from "@/constants/activity-logs.ts";
import type { ActivityLogsFilters } from "@/_store/activity-logs.module.ts";
import { ExportCategory } from "@/constants/exports.ts";
import { ReportType } from "@/constants/reports.ts";

/**
 * Raw devices filter shape as it arrives from / is sent to the backend.
 * Same as `DevicesFilters` but with `labels` replaced by a flat `string[]` and an extra
 * `deviceStatuses` field — the frontend merges these back into a unified `labels` array.
 */
type RawDevicesExportFilters = Omit<DevicesFilters, "labels"> & {
  labels?: string[];
  deviceStatuses?: string[];
};

/**
 * Raw activity logs filter shape as it arrives from / is sent to the backend.
 * Same as `ActivityLogsFilters` but `types` is `string[]` of uppercase keys (e.g. `"CLOUD_APPS"`)
 * rather than `ActivityLogsType[]` enum values (e.g. `ActivityLogsType.CLOUD_APPS === "cloudApps"`).
 */
type RawActivityLogsExportFilters = Omit<ActivityLogsFilters, "types"> & {
  types?: string[];
};

/**
 * The shape of a `ScheduledExportItem` as it crosses the backend boundary —
 * `devices` and `activityLogs` filters carry their raw representations.
 */
export type RawScheduledExportItem = Omit<ScheduledExportItem, "filters"> & {
  filters: Omit<ScheduledExportItem["filters"], "devices" | "activityLogs"> & {
    devices: RawDevicesExportFilters;
    activityLogs: RawActivityLogsExportFilters;
  };
};

/**
 * Converts the scheduled reports list filters to the query param shape expected by the backend.
 * Joins the `types` array into a comma-separated `subTypes` string.
 */
export const convertFiltersForBackend = ({ search, types }: ScheduledReportsFilters) => ({
  search,
  subTypes: types?.join(","),
});

/**
 * Dispatches to the appropriate filter converter before sending data to the backend.
 * Each subType may require field reshaping (e.g. enum values → string keys, group label splitting).
 * Unrecognised subTypes are passed through unchanged.
 */
export const convertDataForBE = (data: ScheduledExportItem | ScheduledReportItem) => {
  if (data.type === ScheduledType.EXPORT) {
    switch (data.subType) {
      case ExportCategory.ACTIVITY_LOGS:
        return convertActivityLogsExportFilters(data);
      case ExportCategory.DEVICES:
        return convertDevicesExportFilters(data);
      default:
        return data;
    }
  }
  if (data.type === ScheduledType.REPORT) {
    switch (data.subType) {
      // Executive Summary report does not require any reshaping
      case ReportType.EXECUTIVE_SUMMARY:
        return data;
    }
  }
  return data;
};

/**
 * Converts `ActivityLogsType` enum values in `filters.activityLogs.types` to the string keys
 * the backend expects (e.g. `ActivityLogsType.CLOUD_APPS` → `"CLOUD_APPS"`).
 */
const convertActivityLogsExportFilters = (data: ScheduledExportItem) => ({
  ...data,
  filters: {
    ...data.filters,
    activityLogs: {
      ...data.filters.activityLogs,
      types: mapActivityLogsTypesToKeyArray(data.filters.activityLogs.types ?? []),
    } as RawActivityLogsExportFilters,
  },
});

/**
 * Maps the frontend device filter onto the `labels` / `deviceStatuses` arrays expected by the
 * backend (the filter stores device statuses under `statuses`).
 */
const convertDevicesExportFilters = (data: ScheduledExportItem) => {
  const { vulnerabilities, osVersion, clientVersion, associated, statuses, labels } =
    data.filters.devices;

  return {
    ...data,
    filters: {
      ...data.filters,
      devices: {
        labels,
        deviceStatuses: statuses,
        vulnerabilities,
        osVersion,
        clientVersion,
        associated,
      } satisfies RawDevicesExportFilters,
    },
  };
};

/**
 * Maps an array of `ActivityLogsType` enum values to the string key names used by the backend
 * (e.g. `ActivityLogsType.CLOUD_APPS` → `"CLOUD_APPS"`).
 */
export const mapActivityLogsTypesToKeyArray = (types: ActivityLogsType[]): string[] => {
  const valueToKey = Object.entries(ActivityLogsType).reduce(
    (acc, [key, value]) => {
      acc[value] = key;
      return acc;
    },
    {} as Record<string, string>
  );

  return types.map((type) => valueToKey[type]);
};

/**
 * Dispatches to the appropriate filter reverter after loading data from the backend.
 * Mirrors `convertDataForBE` — each subType that was reshaped on the way out must be
 * restored to the frontend representation on the way in.
 * Unrecognised subTypes are passed through unchanged.
 */
export const revertDataFromBE = (data: ScheduledExportItem | ScheduledReportItem) => {
  if (data.type === ScheduledType.EXPORT) {
    // Backend sends raw shapes for `devices` and `activityLogs`;
    // the dispatcher's input type lies about them being already-normalized.
    const rawData = data as unknown as RawScheduledExportItem;
    switch (data.subType) {
      case ExportCategory.ACTIVITY_LOGS:
        return revertActivityLogsExportFilters(rawData);
      case ExportCategory.DEVICES:
        return revertDevicesExportFilters(rawData);
      default:
        return data;
    }
  }
  if (data.type === ScheduledType.REPORT) {
    switch (data.subType) {
      case ReportType.EXECUTIVE_SUMMARY:
        return revertExecutiveSummaryReportFilters(data);
    }
  }
  return data;
};

/**
 * Normalizes a `ScheduledReportItem` returned from the backend, applying default filter values
 * when the `filters` field is absent (e.g. reports created before the filters field was introduced).
 */
const revertExecutiveSummaryReportFilters = (data: ScheduledReportItem): ScheduledReportItem => ({
  ...data,
  filters: cloneDeep(data.filters ?? defaultScheduledReportFilters),
});

/**
 * Reverts backend activity log type string keys back to `ActivityLogsType` enum values
 * (e.g. `"CLOUD_APPS"` → `ActivityLogsType.CLOUD_APPS`).
 */
export const revertActivityLogsExportFilters = (data: RawScheduledExportItem) => ({
  ...data,
  filters: {
    ...data.filters,
    activityLogs: {
      ...data.filters.activityLogs,
      types: mapActivityLogsKeysToTypeArray(data.filters.activityLogs.types ?? []),
    } satisfies ActivityLogsFilters,
  },
});

/**
 * Maps backend string keys back to their corresponding `ActivityLogsType` enum values
 * (e.g. `"CLOUD_APPS"` → `ActivityLogsType.CLOUD_APPS`).
 */
export const mapActivityLogsKeysToTypeArray = (keys: string[]): ActivityLogsType[] => {
  return keys.map((key) => ActivityLogsType[key as keyof typeof ActivityLogsType]);
};

/**
 * Restores the backend's `labels` / `deviceStatuses` arrays onto the frontend devices filter,
 * which keeps group labels (`labels`) and device statuses (`statuses`) as separate `string[]`s.
 * Mirrors `convertDevicesExportFilters`, which maps `statuses` → `deviceStatuses` on the way out.
 */
export const revertDevicesExportFilters = (data: RawScheduledExportItem) => {
  const { labels, deviceStatuses, vulnerabilities, osVersion, clientVersion, associated } =
    data.filters.devices;

  return {
    ...data,
    filters: {
      ...data.filters,
      devices: {
        labels: labels ?? [],
        statuses: deviceStatuses ?? [],
        vulnerabilities,
        osVersion,
        clientVersion,
        associated,
      } satisfies DevicesFilters,
    },
  };
};
